import styles from "./AppLayout.module.css";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { useContext, type JSX, type ReactNode } from "react";
import {
  type ActiveView,
  AppContext,
  AppDispatchContext,
  ConfigurationActionTypes,
  selectPaidServices,
  selectSubsidyDependantCalculationDetails,
} from "../../state";
import { useI18n } from "../../i18n";
import { AdvantageList } from "../AdvantageList";
import { CalculatorForm } from "../CalculatorForm";
import { Container } from "../Container";
import { Content } from "../Content";
import { Heading } from "../Heading";
import { InfoBubbles } from "../InfoBubbles";
import { Hint } from "../Hint";
import { StickyBottomSheet, useIntersectionObserverForStickyBottomSheet } from "../StickyBottomSheet";
import { CtaButton } from "../CTA";
import { BudgetCalculator, BudgetCalculatorResult } from "../BudgetCalculator";
import { ViewSwitcher } from "../ViewSwitcher";
import { LeasingResults } from "./LeasingResults";
import { useScrollHighlight } from "./useScrollHighlight";

const AppLayout = (): JSX.Element => {
  const { t, formatNumber } = useI18n();
  const state = useContext(AppContext);
  const dispatch = useContext(AppDispatchContext);

  const { ref: bubbleSectionRef, shouldDisplayStickyBottomSheet } = useIntersectionObserverForStickyBottomSheet();

  const showHeader = state.configuration.showHeader;
  const showBudgetCalculator = state.configuration.showBudgetCalculator;
  const activeView = state.configuration.activeView;

  const handleViewSwitch = (view: ActiveView) => {
    dispatch({ type: ConfigurationActionTypes.SET_ACTIVE_VIEW, payload: view });
  };

  const { scrollTargetRefs, bubbleClickHandler, subsidyBoxClickHandler } = useScrollHighlight();

  const appClasses = classNames(styles.foundation, styles.typography, styles.customVariables, styles.app);
  const layoutContainerClasses = classNames(styles.layoutContainer, { [styles.noMargins]: true });

  const subsidyDependantCalculationDetails = selectSubsidyDependantCalculationDetails(state);
  const { companyPays } = selectPaidServices(state);

  const wrapInTabPanel = (view: ActiveView, content: ReactNode): ReactNode =>
    showBudgetCalculator ? (
      <div role="tabpanel" id={`tabpanel-${view}`} aria-labelledby={`tab-${view}`}>
        {content}
      </div>
    ) : (
      content
    );

  return (
    <div className={appClasses}>
      <div className={layoutContainerClasses}>
        {showBudgetCalculator && showHeader && activeView === "budget" && (
          <Container reducedGap>
            <Content reducedGap>
              <Heading as="h1">
                <Trans i18nKey={"budgetCalculator.heading"} />
              </Heading>
              <Hint>{t("budgetCalculator.hint")}</Hint>
            </Content>
          </Container>
        )}
        {showHeader && activeView === "leasing" && (
          <Container reducedGap fullWidth>
            <Content reducedGap>
              <Heading as="h1">
                <Trans i18nKey={"jobRadCalculator"} />
              </Heading>
              <AdvantageList list={[{ label: "allKilometersFree" }, { label: "privateUse" }, { label: "noDeposit" }]} />
            </Content>
          </Container>
        )}
        <Container increasedGap={activeView === "leasing"}>
          {showBudgetCalculator && <ViewSwitcher activeView={activeView} onSwitch={handleViewSwitch} />}
          <Content background="gray" withPadding>
            {activeView === "leasing" &&
              wrapInTabPanel("leasing", <CalculatorForm scrollTargetRef={scrollTargetRefs.individualSubsidyRef} />)}
            {activeView === "budget" && wrapInTabPanel("budget", <BudgetCalculator />)}
          </Content>
        </Container>
        {activeView === "budget" && (
          <Container breakOutSticky>
            <Content background="gray" withLargePadding className={styles.budgetResultContent}>
              <Heading as="h2" className={styles.budgetResultHeading}>
                {t("budgetCalculator.result.heading")}
              </Heading>
              {state.budgetCalculation.hasCalculated &&
              state.budgetCalculation.result &&
              state.budgetCalculation.result.maxBikePrice >= state.configuration.frameContract.price_min ? (
                <BudgetCalculatorResult result={state.budgetCalculation.result} />
              ) : (
                <Hint variant="highlighted">
                  <Trans
                    i18nKey="budgetCalculator.result.belowMinPrice"
                    values={{ minBikePrice: formatNumber(state.configuration.frameContract.price_min) }}
                  />
                </Hint>
              )}
              <Hint>{t("budgetCalculator.hintFootnote")}</Hint>
            </Content>
          </Container>
        )}
        {activeView === "leasing" && (
          <LeasingResults
            bubbleSectionRef={bubbleSectionRef}
            scrollTargetRefs={scrollTargetRefs}
            bubbleClickHandler={bubbleClickHandler}
            subsidyBoxClickHandler={subsidyBoxClickHandler}
          />
        )}
      </div>
      {activeView === "leasing" && (
        <StickyBottomSheet isVisible={shouldDisplayStickyBottomSheet}>
          <InfoBubbles
            monthlyRate={subsidyDependantCalculationDetails?.netChargeEmployee}
            bikeSavings={subsidyDependantCalculationDetails?.savingsBetweenPrivateAndJobrad}
            bubbleClickHandler={bubbleClickHandler}
            companyPays={companyPays}
            variant="mini"
          />
          {state.configuration.elementVisibility.bottomSheetCtaButton && (
            <CtaButton id="JobRadRechner_BottomSheet_CTA_FHS" variant="forBottomSheet"></CtaButton>
          )}
        </StickyBottomSheet>
      )}
    </div>
  );
};

export { AppLayout };
